/**
 * @file src/utils/pushSubscription.js
 * Browser Web Push helper. Fully feature-detected and guarded: on any
 * unsupported browser, denied permission, or disabled server config it returns
 * a friendly status object instead of throwing. The UI reflects that status.
 *
 * Status shapes returned by enablePush():
 *   { ok: true, status: 'granted' }
 *   { ok: false, status: 'unsupported' | 'denied' | 'disabled' | 'error', reason }
 */
import { notifications } from '../api/notifications';

const SW_URL = '/sw.js';

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// Current permission without prompting: 'granted' | 'denied' | 'default' | 'unsupported'.
export function currentPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

// VAPID public keys are URL-safe base64; the browser needs the raw bytes.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function registerServiceWorker() {
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

/**
 * Register the SW, request permission, subscribe with PushManager, and persist
 * the subscription server-side. Safe to call repeatedly (idempotent upsert).
 */
export async function enablePush() {
  if (!isPushSupported()) {
    return { ok: false, status: 'unsupported', reason: 'This browser does not support push notifications.' };
  }

  try {
    // Ask the server for the VAPID key first — if push is disabled server-side
    // there's nothing to subscribe to and we skip the permission prompt.
    const vapid = await notifications.pushVapidKey();
    if (!vapid?.enabled || !vapid?.publicKey) {
      return { ok: false, status: 'disabled', reason: 'Desktop notifications are not enabled on the server yet.' };
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return {
        ok: false,
        status: permission === 'denied' ? 'denied' : 'default',
        reason: 'Notification permission was not granted.'
      };
    }

    const registration = await registerServiceWorker();
    await navigator.serviceWorker.ready;

    // Reuse an existing subscription if present; otherwise create one.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey)
      });
    }

    const subJson = subscription.toJSON();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    await notifications.pushSubscribe(subJson, userAgent);

    return { ok: true, status: 'granted' };
  } catch (error) {
    return { ok: false, status: 'error', reason: error?.message || 'Failed to enable push notifications.' };
  }
}

/**
 * Unsubscribe locally and remove the subscription server-side.
 */
export async function disablePush() {
  if (!isPushSupported()) {
    return { ok: false, status: 'unsupported' };
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_URL);
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    if (subscription) {
      const endpoint = subscription.endpoint;
      try {
        await subscription.unsubscribe();
      } catch (_) {
        /* best-effort local unsubscribe */
      }
      if (endpoint) {
        try {
          await notifications.pushUnsubscribe(endpoint);
        } catch (_) {
          /* server prune is best-effort — 404/410 also handled server-side on send */
        }
      }
    }
    return { ok: true, status: 'default' };
  } catch (error) {
    return { ok: false, status: 'error', reason: error?.message || 'Failed to disable push notifications.' };
  }
}
